
import {
  Component,
  Mixins,
} from 'vue-property-decorator';
import {
  OperationChildUser,
  AssignmentAssignee,
  AssignmentEntry,
  PermissionCode,
} from '@/types';
import OperationAssignmentsDialog from '@/components/OperationAssignmentsDialog.vue';
import AssignmentEntryItem from '@/components/AssignmentEntryItem.vue';
import OperationComponentMixin from '@/mixins/OperationComponentMixin.vue';
import { getAssignmentLabel } from '@/helpers';

@Component({
  components: {
    OperationAssignmentsDialog,
    AssignmentEntryItem,
  },
})
export default class OperationPmInstaller extends Mixins(OperationComponentMixin) {
  editDialog = false

  mounted() {
    // Carica la configurazione degli assignment types se non è già presente
    // (necessaria al dialog per sapere se 'installer' è multi-assegnatario).
    if (this.$store.getters.getAssignmentTypes.length === 0) {
      this.$store.dispatch('fetchAssignmentTypes');
    }
  }

  // Richiedente della Lead figlia
  get requester(): OperationChildUser | null {
    return this.operationChild?.requester ?? null;
  }

  get requesterName() {
    return this.operationChild?.requester?.name ?? '';
  }

  // L'incarico installatore si applica solo alle figlie elettrica/termica
  // (coerente con targetChildTypes in Assignment.assigneeTypes e con il
  // gating di enea/dogane in OperationAssignments.vue). Per le figlie
  // generiche non va mostrato né aggiungibile.
  get isInstallerApplicable(): boolean {
    return ['elettrica-figlia', 'termica-figlia'].includes(this.operationChild?.type ?? '');
  }

  get installerConfig() {
    return this.$store.getters.getAssignmentTypes.find((t: any) => t.id === 'installer');
  }

  // "Installatore:" se una sola voce, "Installatori:" se più assegnatari.
  get installerTitle(): string {
    return this.installerEntries.length > 1 ? 'Installatori:' : 'Installatore:';
  }

  // Una entry AssignmentEntryItem per ciascun assegnatario installatore
  // (più righe nella cella). Ogni entry è "singola" (multipleAssignees:false)
  // così l'item mostra nome + colore di stato del singolo installatore.
  // Vuoto quando il tipo figlia non è applicabile, manca la config o il permesso.
  get installerEntries(): AssignmentEntry[] {
    const child = this.operationChild;
    if (!child || !this.isInstallerApplicable) return [];
    const config = this.installerConfig;
    if (!config || !this.hasPermission('installerAssignment.show')) return [];

    const base = {
      type: 'installer',
      label: getAssignmentLabel(config, child.type),
      icon: config.icon || 'mdi-account-hard-hat',
      dateTooltip: 'Data assegnazione installatore',
      permissionShow: 'installerAssignment.show',
      permissionEdit: 'installerAssignment.edit',
      multipleAssignees: false,
      assignees: [] as AssignmentAssignee[],
    };

    // Stato "Non applicabile" a livello di incarico
    if ((child.installerAssigned ?? null) === 'na') {
      return [{
        ...base, assigneeId: null, assigneeName: null, assigned: 'na', assignedDate: null,
      }];
    }

    const assignees = this.parseAssignees(child.installerAssignees);
    // Nessun installatore: voce "Da assegnare" cliccabile per aggiungerne
    if (assignees.length === 0) {
      return [{
        ...base, assigneeId: null, assigneeName: null, assigned: 'todo', assignedDate: null,
      }];
    }

    return assignees.map((a) => ({
      ...base,
      assigneeId: a.assigneeId,
      assigneeName: a.assigneeName,
      assigned: a.status,
      assignedDate: a.assignedDate ? new Date(a.assignedDate) : null,
    }));
  }

  parseAssignees(raw: AssignmentAssignee[] | string | null | undefined): AssignmentAssignee[] {
    if (!raw) return [];
    if (typeof raw === 'string') {
      try { return JSON.parse(raw); } catch { return []; }
    }
    return raw;
  }

  get isRequester() {
    return this.user && this.operationChild && this.user.id === this.operationChild.requester.id;
  }

  get isEditable() {
    if (this.user?.isSuperUser) return true;

    if (!this.isCurrentUserOwnerOrRequester) return false;

    return (this.isRequester && this.operationChild?.status === 'new')
      || this.hasPermission('leads.modify_owners');
  }

  iconColor(user: OperationChildUser | null): string {
    if (user?.isDisabled === true) return 'grey lighten-2';
    if (user?.isVirtual === true) return 'light-blue lighten-2';
    if (user?.isDisabled === false) return 'success';

    return 'grey';
  }

  tooltipText(prefix: string, user: OperationChildUser | null): string {
    let result = prefix;

    if (user?.isDisabled === true) result += ' (Disattivato)';
    if (user?.isVirtual === true) result += ' (Virtuale)';

    return result;
  }

  onEdit() {
    this.$emit('edit');
  }

  onEditInstaller(entry: AssignmentEntry) {
    if (!this.hasPermission(entry.permissionEdit as PermissionCode)) return;
    if (!this.isCurrentUserOwnerOrRequester) return;
    this.editDialog = true;
  }
}
